/**
 * ExportDataSection - Seccion para exportar datos del usuario
 *
 * Diseñado para neurodivergencia:
 * - Proceso simple y claro
 * - Feedback inmediato sobre que se va a exportar
 * - Control total sobre tus datos
 */

import { useState, useEffect } from 'react';
import { Download, FileJson, FileSpreadsheet, Loader2 } from 'lucide-react';
import { Button } from '@/ui/primitives';
import { exportToJSON, exportToCSV, getExportSummary } from '@/services/export';
import { showSuccess, showError } from '@/store';
import { feedbackSuccess } from '@/services/feedback';
import { trackFeatureUsed } from '@/services/achievements';

interface ExportSummary {
  protocolCount: number;
  subtaskCount: number;
  checklistCount: number;
  historyDays: number;
  estimatedSize: string;
}

export function ExportDataSection() {
  const [summary, setSummary] = useState<ExportSummary | null>(null);
  const [isExporting, setIsExporting] = useState<'json' | 'csv' | null>(null);
  const [isLoadingSummary, setIsLoadingSummary] = useState(true);

  useEffect(() => {
    const loadSummary = async () => {
      try {
        const data = await getExportSummary();
        setSummary(data);
      } catch (error) {
        console.error('Error loading export summary:', error);
      } finally {
        setIsLoadingSummary(false);
      }
    };
    loadSummary();
  }, []);

  const handleExportJSON = async () => {
    setIsExporting('json');
    try {
      await exportToJSON();
      feedbackSuccess();
      showSuccess('Datos exportados como JSON');
      trackFeatureUsed('export');
    } catch (error) {
      console.error('Export error:', error);
      showError('Error al exportar datos');
    } finally {
      setIsExporting(null);
    }
  };

  const handleExportCSV = async () => {
    setIsExporting('csv');
    try {
      await exportToCSV();
      feedbackSuccess();
      showSuccess('Datos exportados como CSV');
      trackFeatureUsed('export');
    } catch (error) {
      console.error('Export error:', error);
      showError('Error al exportar datos');
    } finally {
      setIsExporting(null);
    }
  };

  return (
    <section>
      <h3 className="text-sm font-medium text-slate-300 mb-3 flex items-center gap-2">
        <Download className="w-4 h-4" />
        Exportar mis datos
      </h3>

      <div className="space-y-4">
        {/* Resumen de datos */}
        <div className="p-3 rounded-lg bg-slate-800/30 text-sm">
          {isLoadingSummary ? (
            <div className="flex items-center gap-2 text-slate-500">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Calculando...</span>
            </div>
          ) : summary ? (
            <div className="space-y-1 text-slate-400">
              <p>
                <span className="text-slate-300">{summary.protocolCount}</span> protocolos con{' '}
                <span className="text-slate-300">{summary.subtaskCount}</span> subtareas
              </p>
              <p>
                <span className="text-slate-300">{summary.checklistCount}</span> listas de verificacion
              </p>
              <p>
                <span className="text-slate-300">{summary.historyDays}</span> dias de historial
              </p>
              <p className="text-xs text-slate-500 mt-2">
                Tamaño estimado: {summary.estimatedSize}
              </p>
            </div>
          ) : (
            <p className="text-slate-500">No se pudo cargar el resumen</p>
          )}
        </div>

        {/* Botones de exportacion */}
        <div className="flex gap-3">
          <Button
            variant="secondary"
            size="sm"
            onClick={handleExportJSON}
            disabled={isExporting !== null}
            className="flex-1"
          >
            {isExporting === 'json' ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <FileJson className="w-4 h-4 mr-2" />
            )}
            JSON
          </Button>

          <Button
            variant="secondary"
            size="sm"
            onClick={handleExportCSV}
            disabled={isExporting !== null}
            className="flex-1"
          >
            {isExporting === 'csv' ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <FileSpreadsheet className="w-4 h-4 mr-2" />
            )}
            CSV
          </Button>
        </div>

        {/* Nota explicativa */}
        <p className="text-2xs text-slate-600">
          Tus datos son tuyos. Exportalos cuando quieras para backup o para usar en otras apps.
        </p>
      </div>
    </section>
  );
}
